// Giden e-Belgeler listesi — fatura veya irsaliye, draft/queued/sent vs.
// Props:
//   docGroup: 'invoice' (eFatura/eArsiv/eIhracat) veya 'waybill' (eIrsaliye)
//   title:    sayfa başlığı
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import { Button, Modal, FilterBar, ListPageHeader, ActionsMenu } from '../components/ui';

const STATUS_COLORS = {
  draft:     { bg: 'rgba(245,158,11,0.15)',  clr: '#f59e0b' },
  queued:    { bg: 'rgba(100,116,139,0.15)', clr: '#64748b' },
  sending:   { bg: 'rgba(59,130,246,0.15)',  clr: 'var(--info)' },
  sent:      { bg: 'rgba(16,185,129,0.15)',  clr: 'var(--success)' },
  retrying:  { bg: 'rgba(245,158,11,0.15)',  clr: '#f59e0b' },
  failed:    { bg: 'rgba(239,68,68,0.15)',   clr: 'var(--danger)' },
  cancelled: { bg: 'rgba(100,116,139,0.15)', clr: '#64748b' },
};
const STATUS_LABELS = {
  draft:'Taslak (Gönderilmedi)', queued:'Kuyrukta', sending:'Gönderiliyor', sent:'Gönderildi',
  retrying:'Tekrar Denenecek', failed:'Başarısız', cancelled:'İptal',
};
const DOC_TYPE_LABELS = {
  eFatura:'e-Fatura', eArsiv:'e-Arşiv', eIhracat:'e-İhracat',
  eIrsaliye:'e-İrsaliye', eSMM:'e-SMM', eMustahsil:'e-Müstahsil',
};
// Hızlı Bilişim / GİB'den gelen İngilizce durum kodlarını Türkçe'ye çevir
const GIB_LABELS_TR = {
  // GİB sonuç
  succeed:'Başarılı', succeeded:'Başarılı', success:'Başarılı',
  approved:'Onaylandı', accepted:'Kabul Edildi',
  rejected:'Reddedildi', refused:'Reddedildi',
  failed:'Başarısız', error:'Hata',
  pending:'Bekliyor', waiting:'Bekliyor', queued:'Sırada',
  // Zarf durumu
  created:'Oluşturuldu', submitted:'GİB\'e Gönderildi', processed:'İşlendi',
  cancelled:'İptal Edildi', canceled:'İptal Edildi',
  delivered:'Teslim Edildi', sent:'Gönderildi',
  unknown:'Bilinmiyor',
};
const trGib = (s) => {
  if (!s) return '-';
  const k = String(s).toLowerCase().trim();
  return GIB_LABELS_TR[k] || s;
};

const fmt = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) => s ? new Date(s).toLocaleString('tr-TR') : '';
const fmtDateOnly = (s) => s ? new Date(s).toLocaleDateString('tr-TR') : '';

const parsePayload = (r) => {
  if (!r) return {};
  if (r._parsed) return r._parsed;
  try {
    const pj = typeof r.payload_json === 'string' ? JSON.parse(r.payload_json) : (r.payload_json || {});
    r._parsed = pj;
    return pj;
  } catch { return {}; }
};
const getInvoiceType = (r) => {
  const pj = parsePayload(r);
  return pj.invoiceModel?.invoiceheader?.InvoiceTypeCode || pj.InvoiceTypeCode || 'SATIS';
};
const getScenario = (r) => {
  const pj = parsePayload(r);
  return pj.invoiceModel?.invoiceheader?.ProfileID || pj.ProfileID || '-';
};
const getInvoiceDate = (r) => {
  const pj = parsePayload(r);
  return pj.invoiceModel?.invoiceheader?.IssueDate || pj.IssueDate || r.created_at;
};
const getKdv = (r) => {
  const pj = parsePayload(r);
  const taxes = pj.invoiceModel?.invoiceheader?.Taxes || [];
  if (Array.isArray(taxes) && taxes.length) {
    return taxes.reduce((s, t) => s + (parseFloat(t.Tax_Amnt) || 0), 0);
  }
  return 0;
};

export default function EDocumentsListPage({ docGroup = null, title = 'e-Belge Durumu' }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ doc_type: '', status: '' });
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [detail, setDetail] = useState(null);
  const [viewerHtml, setViewerHtml] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, direction: 'out' };
      if (docGroup) params.doc_group = docGroup;
      const r = await api.get('/e-documents/list', { params });
      if (r.data.success) setList(r.data.data || []);
    } finally { setLoading(false); }
  }, [filters, docGroup]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    const r = await api.get('/e-documents/' + row.id);
    if (r.data.success) setDetail({ doc: r.data.data, log: r.data.log });
  };

  const queue = async (row) => {
    if (!window.confirm(`Bu belge Hızlı Bilişim üzerinden GİB'e gönderilecek.\n\nBelge No: ${row.document_number || '-'}\nAlıcı: ${row.receiver_name || '-'}\n\nDevam edilsin mi?`)) return;
    try {
      const r = await api.post('/e-documents/' + row.id + '/queue');
      if (r.data.success) {
        load();
      }
    } catch (e) {
      alert(e.response?.data?.message || 'Hata');
    }
  };

  const retry = async (row) => {
    if (!window.confirm('Tekrar göndermek istediğinizden emin misiniz?')) return;
    await api.post('/e-documents/' + row.id + '/retry');
    load();
  };

  const cancel = async (row) => {
    const reason = window.prompt('İptal sebebi:');
    if (!reason) return;
    const r = await api.post('/e-documents/' + row.id + '/cancel', { reason });
    if (r.data.success) load();
    else alert(r.data.message);
  };

  const viewInvoice = async (row) => {
    if (!row.document_uuid) { alert('Bu belge henüz başarıyla gönderilmemiş (UUID yok)'); return; }
    setViewerLoading(true);
    try {
      const r = await api.get('/e-documents/' + row.id + '/view?format=HTML', { responseType: 'text', transformResponse: x => x });
      if (typeof r.data === 'string' && r.data.length > 0 && r.data.trim().startsWith('<')) {
        setViewerHtml({ title: row.document_number || row.document_uuid, html: r.data });
      } else {
        try { const j = JSON.parse(r.data); alert(j.message || 'Görüntüleme başarısız'); }
        catch { alert('Görüntüleme başarısız'); }
      }
    } catch (e) {
      // responseType:'text' + transformResponse:identity → e.response.data string olabilir
      let msg = e.message;
      const d = e.response?.data;
      if (typeof d === 'string') {
        try { msg = JSON.parse(d).message || msg; } catch { if (d) msg = d.substring(0, 500); }
      } else if (d?.message) {
        msg = d.message;
      }
      alert(msg);
    } finally { setViewerLoading(false); }
  };

  const downloadFile = async (row, format) => {
    if (!row.document_uuid) { alert('Bu belge henüz gönderilmemiş'); return; }
    try {
      const resp = await api.get(`/e-documents/${row.id}/view?format=${format}`, {
        responseType: 'blob', transformResponse: x => x,
      });
      const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data]);
      // Hata mesajı JSON olarak gelmişse blob içeriğini metne çevirip oku
      if (blob.type && blob.type.includes('application/json')) {
        const txt = await blob.text();
        try { const j = JSON.parse(txt); alert(j.message || 'İndirme başarısız'); }
        catch { alert('İndirme başarısız'); }
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (row.document_number || row.document_uuid) + '.' + format.toLowerCase();
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      const data = e.response?.data;
      if (data instanceof Blob) {
        const txt = await data.text();
        try { const j = JSON.parse(txt); alert(j.message || 'İndirme hatası'); }
        catch { alert('İndirme hatası'); }
      } else {
        alert(e.response?.data?.message || e.message);
      }
    }
  };

  const printInvoice = async (row) => {
    if (!row.document_uuid) { alert('Bu belge henüz gönderilmemiş'); return; }
    try {
      const r = await api.get('/e-documents/' + row.id + '/view?format=HTML', { responseType: 'text', transformResponse: x => x });
      if (typeof r.data !== 'string' || !r.data.trim().startsWith('<')) { alert('Yazdırma içeriği alınamadı'); return; }
      const w = window.open('', '_blank', 'width=900,height=1100');
      if (!w) { alert('Popup engellendi'); return; }
      w.document.write(r.data);
      w.document.close();
      w.focus();
      setTimeout(() => { try { w.print(); } catch (_) {} }, 400);
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const emailInvoice = async (row) => {
    if (!row.document_uuid) { alert('Bu belge henüz gönderilmemiş'); return; }
    try {
      const st = await api.get('/e-documents/smtp-status');
      if (!st.data.enabled || !st.data.configured) {
        alert('Mail gönderimi için SMTP ayarları yapılmamış.\n\nAyarlar → SMTP sekmesinden:\n• Sunucu adresi ve port\n• Kullanıcı adı / parola\n• Gönderen e-posta\nalanlarını doldurun ve "Aktif" işaretleyin.');
        return;
      }
      const to = window.prompt(`Alıcı e-posta adresi:\n(Gönderen: ${st.data.from_email || '-'})`, row.receiver_email || '');
      if (!to) return;
      if (!/.+@.+\..+/.test(to)) { alert('Geçersiz e-posta'); return; }
      const r = await api.post('/e-documents/' + row.id + '/email', { to });
      if (r.data.success) alert('E-posta başarıyla gönderildi:\n→ ' + to);
      else alert(r.data.message || 'Gönderilemedi');
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const refreshStatus = async (row) => {
    setRefreshingStatus(row.id);
    try {
      const r = await api.post('/e-documents/' + row.id + '/refresh-status');
      if (r.data.success) {
        const d = r.data.data;
        if (!d.found) {
          alert('Belge henüz Hızlı Bilişim arşivinde bulunamadı (sevk edilmiş olabilir, biraz sonra tekrar deneyin)');
        } else {
          alert(
            'GİB Durumu güncellendi:\n\n' +
            'Zarf Durumu: ' + (d.envelope_exp || trGib(d.envelope_status) || '-') + '\n' +
            'GİB Sonucu: ' + (d.gib_result_exp || trGib(d.gib_result) || '-')
          );
          load();
        }
      }
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    } finally { setRefreshingStatus(null); }
  };

  const remove = async (row) => {
    if (!window.confirm('Bu taslak silinsin mi?')) return;
    try {
      const r = await api.delete('/e-documents/' + row.id);
      if (r.data.success) load();
    } catch (e) {
      alert(e.response?.data?.message || 'Hata');
    }
  };

  // Belge tipi seçeneklerini docGroup'a göre filtrele
  const docTypeOptions = (() => {
    let entries = Object.entries(DOC_TYPE_LABELS);
    if (docGroup === 'invoice')  entries = entries.filter(([k]) => ['eFatura','eArsiv','eIhracat'].includes(k));
    if (docGroup === 'waybill')  entries = entries.filter(([k]) => k === 'eIrsaliye');
    return [{ value: '', label: 'Tümü' }, ...entries.map(([k,v]) => ({ value: k, label: v }))];
  })();

  const draftCount = list.filter(r => r.status === 'draft').length;

  return (
    <div className="stocks-page">
      <ListPageHeader
        title={title}
        unit="belge"
        count={list.length}
        showFilterButton
        filtersActive={showFilterBar}
        onToggleFilters={() => setShowFilterBar(s => !s)}
        onRefresh={load}
        refreshing={loading}
        actions={
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {draftCount > 0 && (
              <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 'var(--r-full)', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 700 }}>
                {draftCount} Taslak
              </span>
            )}
          </span>
        }
      />

      <FilterBar
        active={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          ...(docGroup === 'waybill' ? [] : [
            { key: 'doc_type', type: 'select', label: 'Belge Tipi', options: docTypeOptions },
          ]),
          { key: 'status', type: 'select', label: 'Durum', options: [
            { value: '', label: 'Tümü' }, ...Object.entries(STATUS_LABELS).map(([k,v]) => ({ value: k, label: v })),
          ]},
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey={`e-documents-out-${docGroup || 'all'}`}
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Belge yok"
          data={list}
          rowKey="id"
          onRowClick={openDetail}
          bulkActions={[
            { key: 'send', label: 'Toplu Gönder (GİB)', icon: '📤', variant: 'primary',
              onClick: async (ids, clear) => {
                const eligible = list.filter(r => ids.includes(r.id) && r.status === 'draft');
                if (!eligible.length) { alert('Seçili kayıtlar arasında taslak (gönderilmeye hazır) belge yok'); return; }
                if (!window.confirm(`${eligible.length} adet taslak belge GİB'e gönderilecek. Devam edilsin mi?`)) return;
                let ok = 0, fail = 0;
                for (const r of eligible) {
                  try { const res = await api.post('/e-documents/' + r.id + '/queue'); if (res.data.success) ok++; else fail++; }
                  catch { fail++; }
                }
                alert(`Tamamlandı.\n✓ Başarılı: ${ok}\n✗ Başarısız: ${fail}\nAtlanan (taslak değil): ${ids.length - eligible.length}`);
                clear(); load();
              }
            },
            { key: 'print', label: 'Toplu Yazdır', icon: '🖨', variant: 'ghost',
              onClick: async (ids, clear) => {
                const eligible = list.filter(r => ids.includes(r.id) && r.document_uuid);
                if (!eligible.length) { alert('Seçili kayıtlar arasında yazdırılabilir (gönderilmiş) belge yok'); return; }
                if (!window.confirm(`${eligible.length} belge tek tek yazdırılacak. Tarayıcı popup'ları açacak. Devam?`)) return;
                for (const r of eligible) {
                  try {
                    const resp = await api.get('/e-documents/' + r.id + '/view?format=HTML', { responseType: 'text', transformResponse: x => x });
                    if (typeof resp.data === 'string' && resp.data.trim().startsWith('<')) {
                      const w = window.open('', '_blank', 'width=900,height=1100');
                      if (w) { w.document.write(resp.data); w.document.close(); w.focus(); setTimeout(() => { try { w.print(); } catch (_) {} }, 500); }
                    }
                  } catch (_) {}
                  // Popup'ların art arda açılması için kısa bekleme
                  await new Promise(rsv => setTimeout(rsv, 600));
                }
                clear();
              }
            },
          ]}
          columns={[
            { id: 'type', header: 'Tip', accessor: 'doc_type', sortable: true, width: 100,
              cell: r => <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, background: 'rgba(99,102,241,0.12)', color: '#6366f1', fontWeight: 600 }}>
                {DOC_TYPE_LABELS[r.doc_type] || r.doc_type}
              </span> },
            { id: 'erp_doc_no', header: 'ERP Belge No', accessor: 'erp_doc_no', sortable: true, width: 140,
              cell: r => <span className="stock-code">{r.erp_doc_no || '-'}</span> },
            { id: 'docno_ettn', header: 'Fatura No / ETTN', accessor: 'document_number', sortable: true, width: 220,
              cell: r => (
                <span className="stock-name-cell">
                  <span className="stock-code" style={{ fontWeight: 700 }}>{r.document_number || '-'}</span>
                  <br/>
                  <span style={{ fontSize: 10, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                    {r.document_uuid ? r.document_uuid : <em>ETTN bekleniyor</em>}
                  </span>
                </span>
              ) },
            { id: 'receiver', header: 'Firma Adı', accessor: 'receiver_name', sortable: true,
              cell: r => (
                <span className="stock-name-cell">
                  {r.receiver_name || '-'}
                  {r.receiver_alias && <><br/><span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>✉ {r.receiver_alias}</span></>}
                </span>
              ) },
            { id: 'receiver_vkn', header: 'Vergi Kimlik No', accessor: 'receiver_vkn', sortable: true, width: 130,
              cell: r => <span className="stock-code">{r.receiver_vkn || '-'}</span> },
            { id: 'type_scenario', header: 'Fatura Tipi / Senaryo', sortable: false, width: 150,
              cell: r => (
                <span className="stock-name-cell">
                  <span style={{ fontSize: 11, fontWeight: 600 }}>{getInvoiceType(r)}</span>
                  <br/>
                  <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{getScenario(r)}</span>
                </span>
              ) },
            { id: 'invoice_date', header: 'Fatura Tarihi', accessor: r => getInvoiceDate(r), sortable: true, width: 110,
              cell: r => <span className="stock-secondary">{fmtDateOnly(getInvoiceDate(r))}</span> },
            { id: 'created', header: 'İşlem Tarihi', accessor: 'created_at', sortable: true, width: 140,
              cell: r => <span className="stock-secondary">{fmtDate(r.created_at)}</span> },
            { id: 'kdv', header: 'KDV', accessor: r => getKdv(r), sortable: true, width: 100,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => <span className="stock-secondary">{fmt(getKdv(r))}</span> },
            { id: 'total', header: 'Ödenecek Tutar', accessor: r => parseFloat(r.total_amount) || 0, sortable: true, width: 140,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => <span className="stock-price-cell">{fmt(r.total_amount)} {r.currency_code}</span> },
            { id: 'status', header: 'Fatura Durumu', accessor: 'status', sortable: true, width: 160,
              cell: r => {
                const c = STATUS_COLORS[r.status] || {};
                return (
                  <span className="stock-name-cell">
                    <span style={{ fontSize: '0.7rem', padding: '3px 9px', borderRadius: 'var(--r-full)', fontWeight: 700, background: c.bg, color: c.clr }}>
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                    {r.gib_status && <><br/><span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.15)', color: 'var(--success)', fontWeight: 600, marginTop: 2, display: 'inline-block' }}>{trGib(r.gib_status)}</span></>}
                    {r.retry_count > 0 && <><br/><span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Deneme: {r.retry_count}</span></>}
                  </span>
                );
              } },
            { id: 'actions', header: 'İşlemler', sortable: false, hideable: false, draggable: false, filterable: false, resizable: false, width: 140,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => (
                <span onClick={e => e.stopPropagation()}>
                  <ActionsMenu buttonLabel="Seçiniz ▾" align="right" items={[
                    // Görüntüleme + İndirme
                    { label: 'Faturayı Göster', icon: '👁', onClick: () => viewInvoice(r), hidden: !r.document_uuid },
                    { label: 'Yazdır', icon: '🖨', onClick: () => printInvoice(r), hidden: !r.document_uuid },
                    { label: 'Mail Gönder', icon: '✉', onClick: () => emailInvoice(r), hidden: !r.document_uuid },
                    { separator: true, hidden: !r.document_uuid },
                    { label: 'PDF Olarak İndir', icon: '⬇', onClick: () => downloadFile(r, 'PDF'), hidden: !r.document_uuid },
                    { label: 'XML Olarak İndir', icon: '⬇', onClick: () => downloadFile(r, 'XML'), hidden: !r.document_uuid },
                    { label: 'HTML Olarak İndir', icon: '⬇', onClick: () => downloadFile(r, 'HTML'), hidden: !r.document_uuid },
                    { separator: true, hidden: !r.document_uuid },
                    // Durum / Gönderim
                    { label: 'GİB Durumunu Yenile', icon: '🔄', onClick: () => refreshStatus(r), hidden: r.status !== 'sent' },
                    { label: 'Gönder (GİB\'e)', icon: '📤', variant: 'primary', onClick: () => queue(r), hidden: r.status !== 'draft' },
                    { label: 'Tekrar Dene', icon: '↻', onClick: () => retry(r), hidden: !['failed', 'retrying'].includes(r.status) },
                    { separator: true, hidden: r.status !== 'sent' && r.status !== 'draft' },
                    // Detay + Log
                    { label: 'Detayları / Logu Göster', icon: '📋', onClick: () => openDetail(r) },
                    { separator: true },
                    // Tehlikeli
                    { label: 'İptal Et', icon: '✗', variant: 'danger', onClick: () => cancel(r), hidden: r.status !== 'sent' || !r.document_uuid },
                    { label: 'Taslağı Sil', icon: '🗑', variant: 'danger', onClick: () => remove(r), hidden: r.status !== 'draft' },
                  ]} />
                </span>
              ) },
          ]}
        />
      </div>

      {viewerHtml && (
        <Modal title={`Fatura: ${viewerHtml.title}`} size="xl" onClose={() => setViewerHtml(null)}
               overlayStyle={{ zIndex: 2000 }}
               bodyStyle={{ padding: 0, display: 'flex' }}>
          <iframe srcDoc={viewerHtml.html} style={{ width: '100%', height: '90vh', border: 0, background: '#fff', display: 'block' }} title="Fatura" />
        </Modal>
      )}

      {detail && (
        <Modal title={`Belge Detayı · ${detail.doc.document_number || ''}`} size="lg" onClose={() => setDetail(null)}
               cancelAction={{ label: 'Kapat', onClick: () => setDetail(null) }}
               primaryAction={
                 detail.doc.status === 'draft'
                   ? { label: '📤 Gönder', onClick: async () => { await queue(detail.doc); setDetail(null); } }
                   : detail.doc.document_uuid
                     ? { label: '📄 Faturayı Göster', onClick: () => viewInvoice(detail.doc), loading: viewerLoading }
                     : undefined
               }
               secondaryAction={detail.doc.status === 'sent' ? { label: '🔄 GİB Durumu', onClick: () => refreshStatus(detail.doc), loading: refreshingStatus === detail.doc.id } : undefined}>
          <div className="form-grid" style={{ marginBottom: 16 }}>
            <div><strong>Tip:</strong> {DOC_TYPE_LABELS[detail.doc.doc_type]}</div>
            <div><strong>Durum:</strong> {STATUS_LABELS[detail.doc.status]}</div>
            <div><strong>Alıcı:</strong> {detail.doc.receiver_name} ({detail.doc.receiver_vkn})</div>
            <div><strong>UUID:</strong> <code style={{ fontSize: 11 }}>{detail.doc.document_uuid || '-'}</code></div>
            <div><strong>ETTN:</strong> <code style={{ fontSize: 11 }}>{detail.doc.ettn || '-'}</code></div>
            <div><strong>GİB Durum:</strong> {detail.doc.gib_status || '-'}</div>
            <div><strong>Hata:</strong> <span style={{ color: 'var(--danger)' }}>{detail.doc.status_message || '-'}</span></div>
            <div><strong>Deneme:</strong> {detail.doc.retry_count}</div>
          </div>
          <h4 style={{ marginTop: 16 }}>Geçmiş</h4>
          {detail.log.length === 0 ? <div style={{ color: 'var(--text-tertiary)' }}>Log yok</div> : (
            <table className="stocks-table">
              <thead><tr><th>Tarih</th><th>Eski</th><th>Yeni</th><th>Mesaj</th></tr></thead>
              <tbody>
                {detail.log.map(l => (
                  <tr key={l.id}>
                    <td><span className="stock-secondary">{fmtDate(l.created_at)}</span></td>
                    <td>{l.old_status || '-'}</td>
                    <td><strong>{l.new_status}</strong></td>
                    <td><span className="stock-secondary">{l.message?.substring(0, 200) || ''}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {detail.doc.payload_json && (
            <details style={{ marginTop: 16 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}>JSON Payload (debug)</summary>
              <pre style={{ marginTop: 8, padding: 10, background: 'var(--bg-secondary)', borderRadius: 4, fontSize: 11, maxHeight: 400, overflow: 'auto' }}>
                {JSON.stringify(JSON.parse(detail.doc.payload_json), null, 2)}
              </pre>
            </details>
          )}
        </Modal>
      )}
    </div>
  );
}
